import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { AuthService, CanComponentDeactivate } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { FormHelper } from '@klickdata/core/form';
import { GlobalSearchData } from '@klickdata/core/global-search';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { SideNaveDataTypes, SideNaveActionsTypes, MobileService } from '@klickdata/core/mobile';
import { QuestionTypes } from '@klickdata/core/question';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import {
    Resource,
    ResourceCategoryService,
    ResourceData,
    ResourceService,
    ResourceTypes,
    AppScope,
    ResourceTag,
    ResourceCategory,
} from '@klickdata/core/resource';
import { ResourceItemTypes } from '@klickdata/core/resource-item';
import { ResourceBuilderComponent } from '@klickdata/shared-components/src/resource-builder/resource-builder.component';
import { CreateResourceBaseDirective } from '@klickdata/shared-components/src/resource-create/create-resource-base.directive';
import { ResourceListingSheetComponent } from '@klickdata/shared-components/src/resource-listing-sheet/resource-listing-sheet.component';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-course-manager-create-survey',
    templateUrl: './course-manager-create-survey.component.html',
    styleUrls: ['./course-manager-create-survey.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseManagerCreateSurveyComponent
    extends CreateResourceBaseDirective
    implements OnInit, AfterViewInit, OnDestroy, OnChanges, CanComponentDeactivate
{
    @ViewChild(ResourceBuilderComponent) resourceBuilder: ResourceBuilderComponent;
    @Output() saved: EventEmitter<Resource> = new EventEmitter<Resource>();
    @Output() saving: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() published: EventEmitter<{ res: Resource; isParentSubmission?: boolean }> = new EventEmitter<{
        res: Resource;
        isParentSubmission?: boolean;
    }>();
    @Output() onError: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() bottomToolSheet: boolean;
    public questionTypes = [
        QuestionTypes.CHECKBOX,
        QuestionTypes.RADIO,
        QuestionTypes.INPUT,
        QuestionTypes.TEXTAREA,
        QuestionTypes.AI,
    ];
    public loadingUploader: Subject<boolean> = new Subject<boolean>();
    public isAIUploadedImg: Subject<boolean> = new Subject<boolean>();
    public surveyForm: FormGroup;
    public formats: { [key: string]: string };
    ResourceTypes = ResourceTypes;
    @Input() publishOnly: boolean;
    @Input() publishLabel = $localize`Publish`;
    private publish: boolean;
    public isAdmin$: Observable<boolean>;
    public ResourceItemTypes = ResourceItemTypes;
    public savingQuestions: boolean;
    public defaultResourceGoalCount: number;
    AppScope = AppScope;
    private isParentSubmission: boolean;
    @ViewChild('coverImagePanel') coverImagePanel: MatExpansionPanel;
    @ViewChild('mainTabs') mainTabs: MatTabGroup;
    public mediaAIPrompterText: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(
        protected fb: FormBuilder,
        protected auth: AuthService,
        protected route: ActivatedRoute,
        protected categoryService: ResourceCategoryService,
        protected messageService: MessageService,
        protected resourceService: ResourceService,
        protected cdRef: ChangeDetectorRef,
        protected bottomSheet: MatBottomSheet,
        protected dialog: MatDialog,
        protected mobile: MobileService,
        protected mediaService: MediaService,
        protected configService: ConfigService
    ) {
        super(messageService, resourceService, cdRef, dialog);
        this.buildForm();
        this.bottomToolSheet = this.bottomToolSheet || this.route.snapshot.data.bottomToolSheet;
        this.defaultResourceGoalCount = this.configService.config.defaultResourceGoalCharCount;
    }

    ngOnInit() {
        this.isAdmin$ = this.auth.getUser().pipe(
            first(),
            map((user) => user.isAdmin())
        );
    }

    ngOnChanges() {
        if (this.resource) {
            this.update();
        }
    }

    public update() {
        this.surveyForm.patchValue({
            id: this.resource.id,
            priority: this.resource.priority,
            media_id: this.resource.media_id,
            bullets: this.resource.bullets,
            title: this.resource.title,
            instructions: this.resource.instructions,
            description: this.resource.description,
            // category_ids: this.resource.category_ids,
            language_id: this.resource.language_id,
        });
        this.cdRef.markForCheck();
        FormHelper.resetForm(this.surveyForm);
    }

    ngAfterViewInit() {
        if (this.resource) {
            this.updateForm(this.resource);
        } else {
            this.resourceBuilder
                .getResource()
                .pipe(
                    filter((resource) => !!resource),
                    takeUntil(this.destroy)
                )
                .subscribe((resource) => this.updateForm(resource));
        }
    }

    updateForm(resource: Resource): void {
        this.surveyForm.patchValue({
            title: this.surveyForm.value.title ?? resource.title,
            description: resource.description,
            bullets: resource.bullets,
            instructions: resource.instructions,
            tag_ids: resource.tag_ids,
            category_ids: resource.category_ids,
            language_id: resource.language_id,
        });
        FormHelper.resetForm(this.surveyForm);
    }

    public buildForm() {
        this.surveyForm = this.fb.group({
            id: [],
            priority: [],
            media_id: [],
            title: [null, Validators.required],
            description: [null],
            bullets: [null],
            instructions: [null],
            tag_ids: [[]],
            category_ids: [[], Validators.required],
            language_id: [null, Validators.required],
        });

        this.formats = {
            title: $localize`:@@title:Title`,
            description: $localize`:@@description:Description`,
            grade_system_id: $localize`:@@gradeSystem:Grade system`,
            categories: $localize`:@@categories:Categories`,
        };
    }

    /**
     * Save/Publish button action.
     */
    public onSubmit(publish?: boolean) {
        this.publish = publish;
        const itemsReady = this.resourceBuilder.getService().readyForSubmit();
        if (!itemsReady) {
            if (publish) {
                this.messageService.openMessage(MessageErrorComponent, $localize`Questions is required!`);
                this.onError.emit(true);
            } else if (this.surveyForm.dirty) {
                this.submitWithQuestion();
            }
        } else {
            this.savingQuestions = true;
            this.resourceBuilder.getService().submit();
        }
    }

    /**
     * Submit survey after question saved event emitted from RB!
     */
    public submitWithQuestion() {
        this.savingQuestions = false;
        if (this.surveyForm.invalid) {
            FormHelper.markForm(this.surveyForm);
            const inValidsKeys = FormHelper.formatInvalidForm(this.surveyForm, this.formats);
            this.messageService.openMessage(
                MessageErrorComponent,
                inValidsKeys.length < 2 ? $localize`${inValidsKeys} is missing` : $localize`${inValidsKeys} are missing`
            );
            this.setLoading(false);
        } else {
            this.setLoading(true);
            this.prepareForm()
                .pipe(
                    takeUntil(this.destroy),
                    switchMap((data) =>
                        !data.id ? this.resourceService.store(data) : this.resourceService.update(data)
                    )
                )
                .subscribe((resource) => {
                    this.setLoading(false);
                    this.onSaved(resource);
                    this.cdRef.markForCheck();
                });
        }
    }

    private onSaved(resource: Resource) {
        FormHelper.resetForm(this.surveyForm);
        this.messageService.openMessage(MessageSavedComponent);

        if (this.publish) {
            this.published.emit({ res: resource, isParentSubmission: this.isParentSubmission });
            this.isParentSubmission = false;
        } else {
            this.saved.emit(resource);
        }
    }
    public showTagResources(tag: ResourceTag) {
        this.showResources({ id: tag.id, label: tag.name, scope_id: AppScope.TAGS });
    }
    public showCatgeoryResources(category: ResourceCategory) {
        this.showResources({ id: category.id, label: category.title, scope_id: AppScope.CATEGORIES });
    }
    private showResources(item: GlobalSearchData) {
        this.bottomSheet.open(ResourceListingSheetComponent, {
            data: {
                id: item.id,
                label: item.label,
                scope_id: item.scope_id,
            },
            panelClass: 'sheet-wrapper',
        });
    }
    private setLoading(loading: boolean) {
        this.resourceBuilder.getService().setLoading(loading);
        this.saving.emit(loading);
    }

    public prepareForm(): Observable<ResourceData> {
        return combineLatest([this.auth.getUser(), this.resourceBuilder.getResource()]).pipe(
            first(),
            map(([user, resource]) => {
                const data = resource
                    ? resource.getData(this.surveyForm.value)
                    : new Resource(this.surveyForm.value).getData();

                if (!data.id) {
                    data.type_id = ResourceTypes.GeneralSurvey;
                    data.customer_id = user.customer_id;
                    data.author_id = user.id;
                }
                if (this.publish && !data.id) {
                    data.published = moment().format('YYYY-MM-DD HH:mm:ss');
                }
                if (!this.publish && data) {
                    data.last_publish = null;
                }
                return new Resource(data).getData();
            })
        );
    }
    public showImgModal(id: number) {
        this.dialog.open(DialogDisplayImgComponent, {
            maxWidth: '70%',
            maxHeight: '50vh',
            data: id,
        });
    }
    public canDeactivate(): boolean {
        return this.surveyForm.pristine;
    }

    @HostListener('window:beforeunload', ['$event'])
    handleClose($event) {
        $event.returnValue = this.canDeactivate();
    }

    public addMedia(errorMsg?: string) {
        if (this.surveyForm.value.media_id) {
            this.mainTabs.selectedIndex = 1;
            this.coverImagePanel.open();
            return;
        }
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'add_photo_alternate',
                title: $localize`Add an image`,
                description: $localize`Please select your preferred way to add an image.`,
                type: ResourceTypes.SURVEY,
                errorMsg: errorMsg,
            },
        });
        this.getMediaTypeFromSlideIn();
    }
    private getMediaTypeFromSlideIn() {
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobile.getSideNavResponseData()),
                filter((data) => data.type === ResourceTypes.SURVEY),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data && data.value) {
                    this.surveyForm.patchValue({ media_id: data.mediaId });
                    if (data.value === ResourceMediaTypes.FILE) {
                        this.isAIUploadedImg.next(false);
                        this.mediaAIPrompterText.next(null);
                    } else {
                        this.loadingUploader.next(true);
                        this.initAIUpload(data);
                    }
                    this.surveyForm.markAsDirty();
                }
            });
    }
    private initAIUpload(data: any) {
        this.mediaService
            .uploadMediaByLink({
                prompt: data.aiText,
                type: MediaType.PROMPT,
                scope_id: ResourceTypes.scopeByChildType(ResourceTypes.SURVEY),
            })
            .pipe(
                takeUntil(this.destroy),
                catchError((err) => {
                    this.handleAIRequestFailure(err);
                    return of(null);
                })
            )
            .subscribe((media) => {
                this.loadingUploader.next(false);
                if (media && media.id) {
                    this.isAIUploadedImg.next(true);
                    this.surveyForm.patchValue({ media_id: media.id });
                    this.mediaAIPrompterText.next(data.aiText);
                }
            });
    }
    handleAIRequestFailure(err) {
        if (err && err.error && err.error.error) {
            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
            this.addMedia(err.error.error.messages.join('/n'));
        }
    }
    public ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
